/* eslint-disable no-unused-vars */
// =============================================================================
// CampaignAIContentAssistantScreen.js — FULL REBUILD
// JobEng — Business / Marketing
// Location: app/screens/business/marketing/CampaignAIContentAssistantScreen.js
//
// FIXED: AI now uses the server-side /api/ai Groq proxy. The old version
//        called api.anthropic.com directly from the app with no key — it
//        failed every time in production and silently dropped to canned
//        sample text with hardcoded JOBENG branding.
// FIXED: "Post" no longer pretends to publish to LinkedIn/Twitter (those need
//        OAuth APIs we don't have). It now does what's actually possible:
//        per-platform copy + native share sheet, with honest messaging.
//
// Tabs: Generate · Edit · History · Post
// Live DB: /api/data/campaign_content (GET/POST/DELETE) + local cache fallback
// DB monitor: /health + table ping badge
// =============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  Share,
  Platform,
  KeyboardAvoidingView,
  RefreshControl,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Clipboard from 'expo-clipboard';

// ─── API ─────────────────────────────────────────────────────────────────────

const API_BASE = 'https://api.jobeng.ai';
const API_KEY =
  '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getH = async () => {
  const tok =
    (await AsyncStorage.getItem('auth_token').catch(() => null)) ||
    (await AsyncStorage.getItem('userToken').catch(() => null));
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

const getBid = async () =>
  parseInt(await AsyncStorage.getItem('company_id').catch(() => '0'), 10) || 0;

// ─── Constants ───────────────────────────────────────────────────────────────

const C = {
  primary: '#0e4e96',
  primaryDark: '#093a72',
  primaryLight: '#1e7be2',
  accent: '#EC4899',
  bg: '#F4F6FB',
  card: '#FFFFFF',
  text: '#0F172A',
  sub: '#64748B',
  border: '#E2E8F0',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
};

const CONTENT_TYPES = [
  { id: 'social', label: 'Social Post', icon: 'share-variant', maxChars: 280 },
  { id: 'linkedin', label: 'LinkedIn Post', icon: 'linkedin', maxChars: 3000 },
  { id: 'email', label: 'Email', icon: 'email-outline', maxChars: null },
  { id: 'blog', label: 'Blog Post', icon: 'file-document', maxChars: null },
  { id: 'ad', label: 'Ad Copy', icon: 'bullhorn-outline', maxChars: 500 },
  { id: 'job', label: 'Job Post', icon: 'briefcase-outline', maxChars: 2000 },
];

const TONES = ['Professional', 'Casual', 'Persuasive', 'Authoritative', 'Friendly', 'Urgent', 'Inspirational'];
const LENGTHS = [
  { id: 'short', label: 'Short', hint: 'under 60 words' },
  { id: 'medium', label: 'Medium', hint: '100-180 words' },
  { id: 'long', label: 'Long', hint: '250-400 words' },
];

const QUICK_PROMPTS = {
  social: ['Announce the campaign launch', 'Highlight one key benefit', 'Create urgency to apply'],
  linkedin: ['Thought-leadership post about hiring', 'Company culture spotlight', 'Announce open roles'],
  email: ['Promotional email to candidates', 'Re-engagement email', 'Event invitation'],
  blog: ['Industry trends article intro', 'How-to guide outline', 'Company news post'],
  ad: ['Headline + body for paid ad', '3 ad headline variations', 'Retargeting ad copy'],
  job: ['Full job advert from campaign', 'Job advert hook lines', 'Benefits section'],
};

const REFINE_ACTIONS = [
  { id: 'shorten', label: 'Shorten', icon: 'arrow-collapse', instr: 'Shorten this text significantly while keeping the key message' },
  { id: 'expand', label: 'Expand', icon: 'arrow-expand', instr: 'Expand this text with more detail and persuasive points' },
  { id: 'formal', label: 'More formal', icon: 'account-tie', instr: 'Rewrite this text in a more formal, professional register' },
  { id: 'casual', label: 'More casual', icon: 'emoticon-outline', instr: 'Rewrite this text in a more casual, friendly register' },
  { id: 'hashtags', label: '+ Hashtags', icon: 'pound', instr: 'Keep this text as is but append 6 relevant UK hashtags' },
  { id: 'cta', label: 'Stronger CTA', icon: 'gesture-tap-button', instr: 'Rewrite the call to action to be clearer and more compelling' },
];

const PLATFORMS = [
  { id: 'LinkedIn', icon: 'linkedin', color: '#0077B5', limit: 3000 },
  { id: 'Twitter', icon: 'twitter', color: '#1DA1F2', limit: 280 },
  { id: 'Facebook', icon: 'facebook', color: '#1877F2', limit: 63206 },
  { id: 'Instagram', icon: 'instagram', color: '#E1306C', limit: 2200 },
  { id: 'Email', icon: 'email-outline', color: '#0e4e96', limit: null },
];

const TABS = ['Generate', 'Edit', 'History', 'Post'];

const normalizeCampaign = (raw) => ({
  id: raw.id || null,
  name: raw.name || raw.campaign_name || '',
  targetAudience: raw.target_audience || raw.targetAudience || '',
  objective: raw.objective || '',
  platforms: Array.isArray(raw.platforms) ? raw.platforms : [],
});

// =============================================================================
// MODULE-SCOPE COMPONENTS (React.memo)
// =============================================================================

const DbBadge = React.memo(({ status, onPress }) => {
  const cfg =
    status === 'online' ? { c: '#34D399', t: 'Live' }
    : status === 'offline' ? { c: '#F87171', t: 'Offline' }
    : { c: '#FBBF24', t: '…' };
  return (
    <TouchableOpacity style={s.dbBadge} onPress={onPress} activeOpacity={0.7}>
      <View style={[s.dbDot, { backgroundColor: cfg.c }]} />
      <Text style={s.dbText}>{cfg.t}</Text>
    </TouchableOpacity>
  );
});

const TypeCard = React.memo(({ type, active, onPress }) => (
  <TouchableOpacity
    style={[s.typeCard, active && s.typeCardActive]}
    onPress={() => onPress(type.id)}
    activeOpacity={0.7}
  >
    <MaterialCommunityIcons name={type.icon} size={20} color={active ? '#fff' : C.primary} />
    <Text style={[s.typeLabel, active && { color: '#fff' }]}>{type.label}</Text>
    {type.maxChars && <Text style={[s.typeLimit, active && { color: 'rgba(255,255,255,0.7)' }]}>≤{type.maxChars}</Text>}
  </TouchableOpacity>
));

const Chip = React.memo(({ label, active, onPress, small }) => (
  <TouchableOpacity style={[s.chip, small && s.chipSmall, active && s.chipActive]} onPress={onPress} activeOpacity={0.7}>
    <Text style={[s.chipText, small && { fontSize: 11 }, active && s.chipTextActive]}>{label}</Text>
  </TouchableOpacity>
));

const SectionTitle = React.memo(({ children, right }) => (
  <View style={s.sectionTitleRow}>
    <Text style={s.sectionTitle}>{children}</Text>
    {right || null}
  </View>
));

const HistoryCard = React.memo(({ item, onLoad, onDelete }) => {
  const type = CONTENT_TYPES.find((t) => t.id === item.content_type);
  return (
    <TouchableOpacity style={s.historyCard} onPress={() => onLoad(item)} activeOpacity={0.7}>
      <View style={s.historyIcon}>
        <MaterialCommunityIcons name={type?.icon || 'file-document'} size={20} color={C.primary} />
      </View>
      <View style={{ flex: 1, marginHorizontal: 10 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <Text style={s.historyType}>{type?.label || item.content_type}</Text>
          {!!item.tone && <Text style={s.historyTone}>{item.tone}</Text>}
        </View>
        <Text style={s.historyPreview} numberOfLines={2}>{item.content}</Text>
        <Text style={s.historyDate}>{item.created_at ? new Date(item.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</Text>
      </View>
      <TouchableOpacity onPress={() => onDelete(item)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
        <Ionicons name="trash-outline" size={16} color={C.danger} />
      </TouchableOpacity>
    </TouchableOpacity>
  );
});

// =============================================================================
// MAIN SCREEN
// =============================================================================

export default function CampaignAIContentAssistantScreen({ navigation, route }) {
  const campaign = useMemo(() => normalizeCampaign(route?.params?.campaign || {}), [route]);

  const [activeTab, setActiveTab] = useState('Generate');
  const [contentType, setContentType] = useState('social');
  const [tone, setTone] = useState('Professional');
  const [length, setLength] = useState('medium');
  const [prompt, setPrompt] = useState('');
  const [generated, setGenerated] = useState('');
  const [generating, setGenerating] = useState(false);
  const [refining, setRefining] = useState(null); // refine action id in flight
  const [saving, setSaving] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');

  const [selectedPlatforms, setSelectedPlatforms] = useState([]);

  const [dbStatus, setDbStatus] = useState('checking');
  const [dbDetail, setDbDetail] = useState('');

  const typeInfo = CONTENT_TYPES.find((t) => t.id === contentType) || CONTENT_TYPES[0];

  // ---------------------------------------------------------------------------
  // DB CHECK
  // ---------------------------------------------------------------------------

  const checkDb = useCallback(async () => {
    setDbStatus('checking');
    try {
      const h = await getH();
      const t0 = Date.now();
      const health = await fetch(`${API_BASE}/health`, { headers: h }).then((r) => r.ok).catch(() => false);
      const table = await fetch(`${API_BASE}/api/data/campaign_content?limit=1`, { headers: h }).then((r) => r.ok).catch(() => false);
      const ms = Date.now() - t0;
      if (health && table) { setDbStatus('online'); setDbDetail(`API + campaign_content table reachable · ${ms}ms`); }
      else if (health) { setDbStatus('offline'); setDbDetail('API up, campaign_content table unreachable — the table may not exist yet'); }
      else { setDbStatus('offline'); setDbDetail('API not responding — check PM2 / nginx'); }
    } catch (e) { setDbStatus('offline'); setDbDetail(e?.message || 'Network error'); }
  }, []);

  const showDbDetail = useCallback(() => {
    Alert.alert(
      dbStatus === 'online' ? '✅ Connected' : '⚠️ Connection issue',
      dbDetail || 'Checking…',
      [{ text: 'Re-check', onPress: checkDb }, { text: 'OK' }]
    );
  }, [dbStatus, dbDetail, checkDb]);

  // ---------------------------------------------------------------------------
  // HISTORY (server first, cache fallback)
  // ---------------------------------------------------------------------------

  const cacheKey = `ai_content_${campaign.id || 'all'}`;

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    let loaded = false;
    try {
      const h = await getH();
      const bid = await getBid();
      const q = campaign.id ? `campaign_id=${campaign.id}` : `company_id=${bid}`;
      const res = await fetch(`${API_BASE}/api/data/campaign_content?${q}&limit=50`, { headers: h })
        .then((r) => r.json()).catch(() => null);
      const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : null;
      if (rows) {
        const list = rows.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
        setHistory(list);
        AsyncStorage.setItem(cacheKey, JSON.stringify(list.slice(0, 50))).catch(() => {});
        loaded = true;
      }
    } catch {}
    if (!loaded) {
      const raw = await AsyncStorage.getItem(cacheKey).catch(() => null);
      if (raw) { try { setHistory(JSON.parse(raw)); } catch {} }
    }
    setHistoryLoading(false);
  }, [campaign.id, cacheKey]);

  useEffect(() => { checkDb(); loadHistory(); }, [checkDb, loadHistory]);

  const saveContentToDB = useCallback(async ({ type, content, toneUsed, promptUsed, title }) => {
    const bid = await getBid();
    const item = {
      company_id: bid,
      campaign_id: campaign.id || null,
      content_type: type,
      content,
      tone: toneUsed,
      prompt: promptUsed || null,
      title: title || null,
      created_at: new Date().toISOString(),
    };
    let savedId = `local_${Date.now()}`;
    try {
      const h = await getH();
      const res = await fetch(`${API_BASE}/api/data/campaign_content`, {
        method: 'POST', headers: h, body: JSON.stringify(item),
      });
      const json = await res.json().catch(() => ({}));
      if (json?.data?.id) savedId = json.data.id;
    } catch {}
    const withId = { ...item, id: savedId };
    setHistory((prev) => [withId, ...prev].slice(0, 50));
    const arr = JSON.parse((await AsyncStorage.getItem(cacheKey).catch(() => '[]')) || '[]');
    AsyncStorage.setItem(cacheKey, JSON.stringify([withId, ...arr].slice(0, 50))).catch(() => {});
    return withId;
  }, [campaign.id, cacheKey]);

  const deleteHistoryItem = useCallback((item) => {
    Alert.alert('Delete content', 'Remove this from your library?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete', style: 'destructive',
        onPress: async () => {
          setHistory((prev) => prev.filter((x) => x.id !== item.id));
          const arr = JSON.parse((await AsyncStorage.getItem(cacheKey).catch(() => '[]')) || '[]');
          AsyncStorage.setItem(cacheKey, JSON.stringify(arr.filter((x) => x.id !== item.id))).catch(() => {});
          if (item.id && !String(item.id).startsWith('local_')) {
            const h = await getH();
            fetch(`${API_BASE}/api/data/campaign_content/${item.id}`, { method: 'DELETE', headers: h }).catch(() => {});
          }
        },
      },
    ]);
  }, [cacheKey]);

  // ---------------------------------------------------------------------------
  // AI — server-side /api/ai proxy
  // ---------------------------------------------------------------------------

  const callAI = useCallback(async (userPrompt) => {
    const h = await getH();
    const res = await fetch(`${API_BASE}/api/ai`, {
      method: 'POST', headers: h, body: JSON.stringify({ prompt: userPrompt }),
    }).then((r) => r.json()).catch(() => null);
    if (!res?.content) throw new Error(res?.message || 'AI service unavailable');
    return String(res.content).trim();
  }, []);

  const generate = useCallback(async (promptOverride) => {
    const userPrompt = (promptOverride ?? prompt).trim();
    if (!userPrompt) { Alert.alert('Required', 'Enter a prompt to generate content.'); return; }
    if (promptOverride) setPrompt(promptOverride);
    setGenerating(true);
    try {
      const lenDef = LENGTHS.find((l) => l.id === length);
      const fullPrompt =
        `You are an expert UK marketing copywriter. Write ${typeInfo.label.toLowerCase()} content with a ${tone.toLowerCase()} tone, ${lenDef?.hint || 'medium length'}.` +
        (typeInfo.maxChars ? ` HARD LIMIT: stay under ${typeInfo.maxChars} characters.` : '') +
        (campaign.name ? ` Campaign: "${campaign.name}".` : '') +
        (campaign.targetAudience ? ` Target audience: ${campaign.targetAudience}.` : '') +
        (campaign.objective ? ` Objective: ${campaign.objective}.` : '') +
        ` British English. Plain text only, no markdown symbols.` +
        (contentType === 'social' || contentType === 'linkedin' ? ' Include relevant hashtags at the end.' : '') +
        ` Task: ${userPrompt}`;
      const content = await callAI(fullPrompt);
      setGenerated(content);
      setActiveTab('Edit');
    } catch (e) {
      Alert.alert('AI unavailable', `${e?.message || 'Could not generate content'}. Check the connection badge and try again.`);
    } finally { setGenerating(false); }
  }, [prompt, length, typeInfo, tone, campaign, contentType, callAI]);

  const refine = useCallback(async (action) => {
    if (!generated.trim()) { Alert.alert('Nothing to refine', 'Generate content first.'); return; }
    setRefining(action.id);
    try {
      const fullPrompt =
        `${action.instr}. Keep it as ${typeInfo.label.toLowerCase()} content in British English, plain text, no markdown.` +
        (typeInfo.maxChars ? ` Stay under ${typeInfo.maxChars} characters.` : '') +
        `\n\nText:\n${generated}`;
      const content = await callAI(fullPrompt);
      setGenerated(content);
    } catch (e) {
      Alert.alert('AI unavailable', e?.message || 'Could not refine content.');
    } finally { setRefining(null); }
  }, [generated, typeInfo, callAI]);

  // ---------------------------------------------------------------------------
  // ACTIONS
  // ---------------------------------------------------------------------------

  const saveContent = useCallback(async () => {
    if (!generated.trim()) { Alert.alert('Nothing to save', 'Generate content first.'); return; }
    setSaving(true);
    await saveContentToDB({ type: contentType, content: generated, toneUsed: tone, promptUsed: prompt });
    setSaving(false);
    Alert.alert('✅ Saved', 'Content saved to your library (History tab).');
  }, [generated, contentType, tone, prompt, saveContentToDB]);

  const copyContent = useCallback(async (text) => {
    await Clipboard.setStringAsync(text ?? generated);
    Alert.alert('Copied', 'Content copied to clipboard.');
  }, [generated]);

  const shareContent = useCallback(() => {
    if (!generated.trim()) return;
    Share.share({ message: generated }).catch(() => {});
  }, [generated]);

  const loadHistoryItem = useCallback((item) => {
    setGenerated(item.content || '');
    setContentType(item.content_type || 'social');
    if (item.tone) setTone(item.tone);
    if (item.prompt) setPrompt(item.prompt);
    setActiveTab('Edit');
  }, []);

  const togglePlatform = useCallback((id) =>
    setSelectedPlatforms((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])), []);

  // Honest posting: copy or open native share sheet, record what was prepared
  const prepareForPlatforms = useCallback(async () => {
    if (!generated.trim()) { Alert.alert('Nothing to post', 'Generate content first.'); return; }
    if (!selectedPlatforms.length) { Alert.alert('No platforms', 'Select at least one platform.'); return; }
    const over = selectedPlatforms
      .map((id) => PLATFORMS.find((p) => p.id === id))
      .filter((p) => p?.limit && generated.length > p.limit);
    if (over.length) {
      Alert.alert('Too long', `Content exceeds the limit for: ${over.map((p) => p.id).join(', ')}. Use "Shorten" in the Edit tab.`);
      return;
    }
    await Clipboard.setStringAsync(generated);
    await saveContentToDB({
      type: contentType, content: generated, toneUsed: tone, promptUsed: prompt,
      title: `Prepared for ${selectedPlatforms.join(', ')}`,
    });
    Share.share({ message: generated }).catch(() => {});
  }, [generated, selectedPlatforms, contentType, tone, prompt, saveContentToDB]);

  // ---------------------------------------------------------------------------
  // DERIVED
  // ---------------------------------------------------------------------------

  const filteredHistory = useMemo(() => {
    const q = historySearch.trim().toLowerCase();
    if (!q) return history;
    return history.filter((i) =>
      String(i.content || '').toLowerCase().includes(q) ||
      String(i.content_type || '').toLowerCase().includes(q)
    );
  }, [history, historySearch]);

  const overLimit = typeInfo.maxChars && generated.length > typeInfo.maxChars;

  // ---------------------------------------------------------------------------
  // TABS
  // ---------------------------------------------------------------------------

  const renderGenerate = () => (
    <ScrollView style={s.tab} keyboardShouldPersistTaps="handled">
      {!!campaign.name && (
        <View style={s.campaignBanner}>
          <MaterialCommunityIcons name="bullhorn-outline" size={14} color={C.primary} />
          <Text style={s.campaignBannerText} numberOfLines={1}>
            {campaign.name}{campaign.objective ? ` · ${campaign.objective}` : ''}
          </Text>
        </View>
      )}

      <SectionTitle>Content type</SectionTitle>
      <View style={s.typeGrid}>
        {CONTENT_TYPES.map((t) => (
          <TypeCard key={t.id} type={t} active={contentType === t.id} onPress={setContentType} />
        ))}
      </View>

      <SectionTitle>Tone</SectionTitle>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.chipRow}>
        {TONES.map((t) => (
          <Chip key={t} label={t} active={tone === t} onPress={() => setTone(t)} />
        ))}
      </ScrollView>

      <SectionTitle>Length</SectionTitle>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        {LENGTHS.map((l) => (
          <Chip key={l.id} label={`${l.label} (${l.hint})`} active={length === l.id} onPress={() => setLength(l.id)} />
        ))}
      </View>

      <SectionTitle>Quick starts</SectionTitle>
      <View style={{ gap: 8 }}>
        {(QUICK_PROMPTS[contentType] || []).map((p) => (
          <TouchableOpacity key={p} style={s.quickPrompt} onPress={() => generate(p)} disabled={generating}>
            <MaterialCommunityIcons name="lightning-bolt-outline" size={14} color={C.accent} />
            <Text style={s.quickPromptText}>{p}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <SectionTitle>Your prompt</SectionTitle>
      <TextInput
        style={[s.input, s.inputMultiline]}
        value={prompt}
        onChangeText={setPrompt}
        placeholder={`Describe the ${typeInfo.label.toLowerCase()} you need…`}
        placeholderTextColor="#9CA3AF"
        multiline
      />
      <TouchableOpacity
        style={[s.generateBtn, (generating || !prompt.trim()) && { opacity: 0.6 }]}
        onPress={() => generate()}
        disabled={generating || !prompt.trim()}
      >
        {generating ? <ActivityIndicator size="small" color="#fff" /> : <MaterialCommunityIcons name="auto-fix" size={18} color="#fff" />}
        <Text style={s.generateBtnText}>{generating ? 'Generating…' : 'Generate Content'}</Text>
      </TouchableOpacity>
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderEdit = () => (
    <ScrollView style={s.tab} keyboardShouldPersistTaps="handled">
      <SectionTitle
        right={
          <Text style={[s.charCounter, overLimit && { color: C.danger }]}>
            {generated.length}{typeInfo.maxChars ? `/${typeInfo.maxChars}` : ''} chars
          </Text>
        }
      >
        {typeInfo.label} · {tone}
      </SectionTitle>

      <TextInput
        style={[s.input, s.editorInput]}
        value={generated}
        onChangeText={setGenerated}
        placeholder="Generated content appears here — or paste your own to refine it."
        placeholderTextColor="#9CA3AF"
        multiline
      />
      {overLimit && <Text style={s.fieldError}>Over the {typeInfo.maxChars}-character limit for {typeInfo.label} — tap "Shorten".</Text>}

      <SectionTitle>AI refine</SectionTitle>
      <View style={s.refineGrid}>
        {REFINE_ACTIONS.map((a) => (
          <TouchableOpacity
            key={a.id}
            style={[s.refineBtn, refining === a.id && { opacity: 0.6 }]}
            onPress={() => refine(a)}
            disabled={!!refining || !generated.trim()}
          >
            {refining === a.id
              ? <ActivityIndicator size="small" color={C.accent} />
              : <MaterialCommunityIcons name={a.icon} size={15} color={C.accent} />}
            <Text style={s.refineText}>{a.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <View style={s.actionRow}>
        <TouchableOpacity style={[s.actionBtn, { backgroundColor: C.primary }]} onPress={saveContent} disabled={saving}>
          {saving ? <ActivityIndicator size="small" color="#fff" /> : <Ionicons name="save-outline" size={16} color="#fff" />}
          <Text style={s.actionBtnText}>Save</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[s.actionBtn, { backgroundColor: C.sub }]} onPress={() => copyContent()}>
          <Ionicons name="copy-outline" size={16} color="#fff" />
          <Text style={s.actionBtnText}>Copy</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[s.actionBtn, { backgroundColor: C.success }]} onPress={shareContent}>
          <Ionicons name="share-outline" size={16} color="#fff" />
          <Text style={s.actionBtnText}>Share</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[s.actionBtn, { backgroundColor: C.accent }]} onPress={() => setActiveTab('Post')}>
          <MaterialCommunityIcons name="send-outline" size={16} color="#fff" />
          <Text style={s.actionBtnText}>Post</Text>
        </TouchableOpacity>
      </View>
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderHistory = () => (
    <ScrollView
      style={s.tab}
      refreshControl={<RefreshControl refreshing={historyLoading} onRefresh={loadHistory} tintColor={C.primary} />}
    >
      <View style={s.searchWrap}>
        <Ionicons name="search" size={15} color={C.sub} />
        <TextInput
          style={s.searchInput}
          value={historySearch}
          onChangeText={setHistorySearch}
          placeholder="Search saved content…"
          placeholderTextColor="#9CA3AF"
        />
      </View>
      {historyLoading && history.length === 0 ? (
        <ActivityIndicator size="large" color={C.primary} style={{ marginTop: 40 }} />
      ) : filteredHistory.length === 0 ? (
        <View style={s.empty}>
          <MaterialCommunityIcons name="history" size={44} color="#CBD5E1" />
          <Text style={s.emptyText}>No saved content yet — generate something and tap Save.</Text>
        </View>
      ) : (
        filteredHistory.map((item) => (
          <HistoryCard key={String(item.id)} item={item} onLoad={loadHistoryItem} onDelete={deleteHistoryItem} />
        ))
      )}
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  const renderPost = () => (
    <ScrollView style={s.tab}>
      <View style={s.honestyNote}>
        <Ionicons name="information-circle-outline" size={15} color={C.primary} />
        <Text style={s.honestyText}>
          Direct auto-posting needs each platform's API access. For now this copies your content and opens the share sheet so you can post in one tap per app.
        </Text>
      </View>

      <SectionTitle>Platforms</SectionTitle>
      <View style={s.platformGrid}>
        {PLATFORMS.map((p) => {
          const sel = selectedPlatforms.includes(p.id);
          const over = p.limit && generated.length > p.limit;
          return (
            <TouchableOpacity
              key={p.id}
              style={[s.platformCard, sel && { backgroundColor: p.color, borderColor: p.color }, over && { borderColor: C.danger }]}
              onPress={() => togglePlatform(p.id)}
              activeOpacity={0.7}
            >
              <MaterialCommunityIcons name={p.icon} size={22} color={sel ? '#fff' : p.color} />
              <Text style={[s.platformName, sel && { color: '#fff' }]}>{p.id}</Text>
              {p.limit ? (
                <Text style={[s.platformLimit, sel && { color: 'rgba(255,255,255,0.75)' }, over && { color: sel ? '#fff' : C.danger, fontWeight: '800' }]}>
                  {generated.length}/{p.limit}
                </Text>
              ) : <Text style={[s.platformLimit, sel && { color: 'rgba(255,255,255,0.75)' }]}>no limit</Text>}
              {sel && <Ionicons name="checkmark-circle" size={15} color="#fff" style={{ position: 'absolute', top: 6, right: 6 }} />}
            </TouchableOpacity>
          );
        })}
      </View>

      <SectionTitle>Content preview</SectionTitle>
      {generated ? (
        <View style={s.previewBox}>
          <Text style={s.previewText}>{generated}</Text>
          <TouchableOpacity style={s.previewCopy} onPress={() => copyContent()}>
            <Ionicons name="copy-outline" size={14} color={C.primary} />
            <Text style={s.previewCopyText}>Copy</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={[s.previewBox, { backgroundColor: '#FFF8E1', borderColor: '#FDE68A' }]}>
          <Text style={{ color: '#92400E', fontSize: 12.5 }}>Generate content first (Generate tab).</Text>
        </View>
      )}

      <TouchableOpacity
        style={[s.generateBtn, { backgroundColor: C.success }, (!generated || !selectedPlatforms.length) && { opacity: 0.5 }]}
        onPress={prepareForPlatforms}
        disabled={!generated || !selectedPlatforms.length}
      >
        <Ionicons name="share-social-outline" size={18} color="#fff" />
        <Text style={s.generateBtnText}>Copy + Open Share Sheet</Text>
      </TouchableOpacity>
      <View style={{ height: 30 }} />
    </ScrollView>
  );

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <SafeAreaView style={s.safe} edges={['top']}>
      <View style={s.header}>
        <TouchableOpacity style={s.headerBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          <Text style={s.headerTitle}>AI Content Assistant</Text>
          <Text style={s.headerSub} numberOfLines={1}>{campaign.name || 'General content'}</Text>
        </View>
        <DbBadge status={dbStatus} onPress={showDbDetail} />
        <MaterialCommunityIcons name="brain" size={22} color="#fff" style={{ marginLeft: 8 }} />
      </View>

      <View style={s.tabBar}>
        {TABS.map((t) => (
          <TouchableOpacity key={t} style={[s.tabBtn, activeTab === t && s.tabBtnActive]} onPress={() => setActiveTab(t)}>
            <Text style={[s.tabText, activeTab === t && s.tabTextActive]}>{t}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        {activeTab === 'Generate' && renderGenerate()}
        {activeTab === 'Edit' && renderEdit()}
        {activeTab === 'History' && renderHistory()}
        {activeTab === 'Post' && renderPost()}
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: C.primary },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.primary, paddingHorizontal: 10, paddingVertical: 10, gap: 6 },
  headerBtn: { padding: 5 },
  headerTitle: { color: '#fff', fontSize: 17, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.65)', fontSize: 11, marginTop: 1 },

  dbBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(255,255,255,0.14)', borderRadius: 12, paddingHorizontal: 9, paddingVertical: 5 },
  dbDot: { width: 7, height: 7, borderRadius: 4 },
  dbText: { color: '#fff', fontSize: 10.5, fontWeight: '700' },

  tabBar: { flexDirection: 'row', backgroundColor: C.primary, paddingHorizontal: 8, paddingBottom: 8, gap: 6 },
  tabBtn: { flex: 1, alignItems: 'center', paddingVertical: 8, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.1)' },
  tabBtnActive: { backgroundColor: '#fff' },
  tabText: { fontSize: 12, fontWeight: '700', color: 'rgba(255,255,255,0.8)' },
  tabTextActive: { color: C.primary },

  tab: { flex: 1, backgroundColor: C.bg, paddingHorizontal: 14, paddingTop: 12 },

  campaignBanner: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#EFF4FF', borderRadius: 10, paddingHorizontal: 10, paddingVertical: 7 },
  campaignBannerText: { flex: 1, fontSize: 12, fontWeight: '700', color: C.primary },

  sectionTitleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, marginBottom: 8 },
  sectionTitle: { fontSize: 14, fontWeight: '800', color: C.text },
  charCounter: { fontSize: 11.5, fontWeight: '700', color: C.sub },

  typeGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  typeCard: { width: '31%', flexGrow: 1, alignItems: 'center', backgroundColor: C.card, borderRadius: 12, paddingVertical: 12, borderWidth: 1, borderColor: C.border },
  typeCardActive: { backgroundColor: C.primary, borderColor: C.primary },
  typeLabel: { fontSize: 10.5, fontWeight: '700', color: C.text, marginTop: 5, textAlign: 'center' },
  typeLimit: { fontSize: 9, color: C.sub, marginTop: 2 },

  chipRow: { gap: 8, paddingVertical: 2 },
  chip: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 15, backgroundColor: C.card, borderWidth: 1, borderColor: C.border },
  chipSmall: { paddingHorizontal: 10, paddingVertical: 5 },
  chipActive: { backgroundColor: C.primary, borderColor: C.primary },
  chipText: { fontSize: 11.5, fontWeight: '600', color: C.sub },
  chipTextActive: { color: '#fff' },

  quickPrompt: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: C.card, borderRadius: 11, paddingHorizontal: 12, paddingVertical: 11, borderWidth: 1, borderColor: C.border },
  quickPromptText: { flex: 1, fontSize: 12.5, fontWeight: '600', color: C.text },

  input: { backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderRadius: 11, paddingHorizontal: 12, paddingVertical: Platform.OS === 'ios' ? 10 : 8, fontSize: 13.5, color: C.text },
  inputMultiline: { minHeight: 90, textAlignVertical: 'top' },
  editorInput: { minHeight: 200, textAlignVertical: 'top', lineHeight: 20 },
  fieldError: { fontSize: 11, color: C.danger, marginTop: 6, fontWeight: '600' },

  generateBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: C.accent, borderRadius: 12, paddingVertical: 13, marginTop: 14 },
  generateBtnText: { color: '#fff', fontSize: 14.5, fontWeight: '800' },

  refineGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  refineBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: '#FCE7F3', borderRadius: 10, paddingHorizontal: 11, paddingVertical: 8 },
  refineText: { fontSize: 11.5, fontWeight: '700', color: C.accent },

  actionRow: { flexDirection: 'row', gap: 8, marginTop: 16 },
  actionBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, borderRadius: 11, paddingVertical: 11 },
  actionBtnText: { color: '#fff', fontSize: 12, fontWeight: '800' },

  searchWrap: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.card, borderRadius: 11, paddingHorizontal: 10, borderWidth: 1, borderColor: C.border, marginBottom: 10 },
  searchInput: { flex: 1, paddingVertical: Platform.OS === 'ios' ? 9 : 7, paddingHorizontal: 8, fontSize: 13.5, color: C.text },

  historyCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.card, borderRadius: 12, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: C.border },
  historyIcon: { width: 38, height: 38, borderRadius: 11, backgroundColor: '#EFF4FF', alignItems: 'center', justifyContent: 'center' },
  historyType: { fontSize: 12, fontWeight: '800', color: C.text },
  historyTone: { fontSize: 9.5, fontWeight: '700', color: C.accent, backgroundColor: '#FCE7F3', borderRadius: 6, paddingHorizontal: 6, paddingVertical: 1 },
  historyPreview: { fontSize: 11.5, color: C.sub, marginTop: 3, lineHeight: 16 },
  historyDate: { fontSize: 9.5, color: '#94A3B8', marginTop: 3 },

  empty: { alignItems: 'center', paddingVertical: 40, paddingHorizontal: 30 },
  emptyText: { fontSize: 12.5, color: C.sub, textAlign: 'center', marginTop: 10, lineHeight: 18 },

  honestyNote: { flexDirection: 'row', alignItems: 'flex-start', gap: 7, backgroundColor: '#EFF4FF', borderRadius: 11, padding: 11 },
  honestyText: { flex: 1, fontSize: 11.5, color: C.primary, lineHeight: 16, fontWeight: '600' },

  platformGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  platformCard: { width: '31%', flexGrow: 1, alignItems: 'center', backgroundColor: C.card, borderRadius: 12, paddingVertical: 13, borderWidth: 1.5, borderColor: C.border },
  platformName: { fontSize: 11.5, fontWeight: '700', color: C.text, marginTop: 5 },
  platformLimit: { fontSize: 9.5, color: C.sub, marginTop: 2 },

  previewBox: { backgroundColor: C.card, borderRadius: 12, padding: 13, borderWidth: 1, borderColor: C.border },
  previewText: { fontSize: 13, color: C.text, lineHeight: 19 },
  previewCopy: { flexDirection: 'row', alignItems: 'center', gap: 4, alignSelf: 'flex-end', marginTop: 8 },
  previewCopyText: { fontSize: 11.5, fontWeight: '800', color: C.primary },
});
